import type { TPlayerDiagnosticFaultCode } from '@lucimark/shared/models/player-diagnostics'
import * as Sentry from '@sentry/vue'

const DURATION_INVARIANT_MESSAGE = 'Player playback duration invariant violated'

const MAX_TAG_LEN = 120

function truncateTag(value: string): string {
  return value.length <= MAX_TAG_LEN ? value : `${value.slice(0, MAX_TAG_LEN - 1)}…`
}

export interface IPlaybackDurationInvariantSentryContext {
  code: TPlayerDiagnosticFaultCode
  detailMs: number
  frameId?: string | null
  frameIndex?: number
  reason?: string | null
  publishedDurationSec?: number
  materializedDurationSec?: number
  workerDurationSec?: number
  overlayDurationSec?: number
  mediaDurationSec?: number
  publishedCampaignTotalSec?: number
  materializedCampaignTotalSec?: number
  queueSequenceHash?: string | null
  scheduleRootUrn?: string | null
  operatorMasterPlaylistUrn?: string | null
}

const lastReportedFingerprintByCode = new Map<string, string>()

function fingerprintForContext(ctx: IPlaybackDurationInvariantSentryContext): string {
  return [
    ctx.code,
    ctx.frameId ?? '',
    String(ctx.frameIndex ?? -1),
    String(ctx.publishedDurationSec ?? ''),
    String(ctx.materializedDurationSec ?? ''),
    String(ctx.workerDurationSec ?? ''),
    String(ctx.overlayDurationSec ?? ''),
    String(ctx.mediaDurationSec ?? ''),
    ctx.queueSequenceHash ?? ''
  ].join('|')
}

/**
 * Rising-edge Sentry issue for playback duration invariant violations.
 * Filter: message `Player playback duration invariant violated`, tag `player_duration_invariant_code`.
 */
export function reportPlayerPlaybackDurationInvariantToSentry(
  ctx: IPlaybackDurationInvariantSentryContext
): void {
  const fingerprint = fingerprintForContext(ctx)
  if (lastReportedFingerprintByCode.get(ctx.code) === fingerprint) {
    return
  }
  lastReportedFingerprintByCode.set(ctx.code, fingerprint)

  try {
    Sentry.addBreadcrumb({
      category: 'player_duration_invariant',
      message: `player_duration_invariant_${ctx.code}`,
      level: 'warning',
      data: {
        code: ctx.code,
        detailMs: ctx.detailMs,
        frameId: ctx.frameId ?? undefined,
        frameIndex: ctx.frameIndex,
        reason: ctx.reason ?? undefined
      }
    })

    Sentry.captureMessage(DURATION_INVARIANT_MESSAGE, {
      level: 'warning',
      tags: {
        player_duration_invariant: 'true',
        player_duration_invariant_code: truncateTag(ctx.code),
        ...(ctx.frameId != null && ctx.frameId !== ''
          ? { player_duration_invariant_frame_id: truncateTag(ctx.frameId) }
          : {}),
        ...(ctx.reason != null && ctx.reason !== ''
          ? { player_duration_invariant_reason: truncateTag(ctx.reason) }
          : {})
      },
      fingerprint: [
        'player-playback-duration-invariant',
        ctx.code,
        ctx.frameId ?? 'no_frame',
        String(ctx.materializedDurationSec ?? ctx.overlayDurationSec ?? 'na')
      ],
      extra: {
        detailMs: ctx.detailMs,
        frameIndex: ctx.frameIndex,
        reason: ctx.reason,
        publishedDurationSec: ctx.publishedDurationSec,
        materializedDurationSec: ctx.materializedDurationSec,
        workerDurationSec: ctx.workerDurationSec,
        overlayDurationSec: ctx.overlayDurationSec,
        mediaDurationSec: ctx.mediaDurationSec,
        publishedCampaignTotalSec: ctx.publishedCampaignTotalSec,
        materializedCampaignTotalSec: ctx.materializedCampaignTotalSec,
        queueSequenceHash: ctx.queueSequenceHash,
        scheduleRootUrn: ctx.scheduleRootUrn,
        operatorMasterPlaylistUrn: ctx.operatorMasterPlaylistUrn
      }
    })
  }
  catch {
    /* Sentry not initialized or unavailable */
  }
}

export function clearPlayerPlaybackDurationInvariantSentryDedupe(code: string): void {
  lastReportedFingerprintByCode.delete(code)
}

/** Test-only reset of dedupe state. */
export function resetPlayerPlaybackDurationInvariantSentryDedupeForTests(): void {
  lastReportedFingerprintByCode.clear()
}
